// HealthScoreCard — headline "how am I doing" tile for a close run.
//
// Renders a large numeric Close Health Score (0-100) with a color band,
// four sub-metric bars (Tier 1 rate, memo discipline, credit pending $,
// aging), and a "Top Opportunity" hint at the bottom. Auto-flip confidence
// folds in as a small footnote rather than its own bar (5% sub-component).
//
// Props:
//   closeResult: object returned by GET /api/mec/close/.../... (may be null)
//   matches:     array of mec_match rows from listMatches() (may be null)
//   loading:     boolean -> render skeleton
//
// Designed to be mounted by Phase 4's LocationDrillDown.jsx. This file
// MUST NOT mount itself anywhere.

import { Activity, TrendingUp, Target, Lightbulb } from 'lucide-react';
import Card from '../ui/Card.jsx';
import Badge from '../ui/Badge.jsx';
import {
  TARGETS,
  computeSubMetrics,
  computeScore,
  scoreBand,
  topOpportunity,
} from './healthScore.js';

// ─── Helpers ─────────────────────────────────────────────────────────────

function fmtPct(x, digits = 0) {
  if (x === null || x === undefined || !isFinite(x)) return '—';
  return `${(x * 100).toFixed(digits)}%`;
}

function fmtMoney(cents) {
  if (cents === null || cents === undefined || !isFinite(cents)) return '—';
  const dollars = cents / 100;
  if (Math.abs(dollars) >= 1000) {
    return `$${(dollars / 1000).toFixed(1)}k`;
  }
  return `$${dollars.toFixed(0)}`;
}

// Map score band -> palette tokens for the headline ring + chip.
const BAND_STYLES = {
  good: {
    chipTone: 'success',
    ringClass: 'border-[var(--color-success-300)]',
    bgClass: 'bg-[var(--color-success-50)]',
    textClass: 'text-[var(--color-success-700)]',
    iconWrapClass: 'bg-[var(--color-success-500)] text-white',
    barClass: 'bg-[var(--color-success-500)]',
    summary: 'Good shape',
  },
  warn: {
    chipTone: 'action',
    ringClass: 'border-[var(--color-action-300)]',
    bgClass: 'bg-[var(--color-action-50)]',
    textClass: 'text-[var(--color-action-700)]',
    iconWrapClass: 'bg-[var(--color-action-500)] text-white',
    barClass: 'bg-[var(--color-action-500)]',
    summary: 'On track',
  },
  bad: {
    chipTone: 'danger',
    ringClass: 'border-[var(--color-danger-300)]',
    bgClass: 'bg-[var(--color-danger-50)]',
    textClass: 'text-[var(--color-danger-700)]',
    iconWrapClass: 'bg-[var(--color-danger-500)] text-white',
    barClass: 'bg-[var(--color-danger-500)]',
    summary: 'Needs attention',
  },
  neutral: {
    chipTone: 'neutral',
    ringClass: 'border-[var(--color-warm-200)]',
    bgClass: 'bg-[var(--color-cream-50)]',
    textClass: 'text-[var(--color-warm-700)]',
    iconWrapClass: 'bg-[var(--color-warm-100)] text-[var(--color-warm-700)]',
    barClass: 'bg-[var(--color-warm-400)]',
    summary: '—',
  },
};

// Per-bar tone: pick a tone based on how close to target the sub-metric is.
function barTone(ratio) {
  // ratio: 0..1 (1 = at or above target)
  if (ratio >= 0.9) return 'bg-[var(--color-success-500)]';
  if (ratio >= 0.6) return 'bg-[var(--color-action-500)]';
  return 'bg-[var(--color-danger-500)]';
}

// ─── Sub-components ──────────────────────────────────────────────────────

function SubMetricBar({ label, currentText, targetText, ratio, loading }) {
  const pct = Math.max(0, Math.min(1, ratio || 0));
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-[12px] font-semibold text-[var(--color-warm-700)]">
          {label}
        </span>
        <span className="text-[12px] tabular-nums text-[var(--color-warm-600)]">
          {loading ? (
            <span className="inline-block w-12 h-3 bg-[var(--color-warm-100)] rounded animate-pulse" />
          ) : (
            <>
              <span className="font-semibold text-[var(--color-warm-900)]">
                {currentText}
              </span>
              <span className="text-[var(--color-warm-400)] mx-1">/</span>
              <span className="inline-flex items-center gap-0.5">
                <Target className="w-3 h-3" />
                {targetText}
              </span>
            </>
          )}
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-[var(--color-warm-100)] overflow-hidden">
        {!loading && (
          <div
            className={`h-full rounded-full transition-all duration-500 ${barTone(pct)}`}
            style={{ width: `${pct * 100}%` }}
          />
        )}
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <Card padded className="overflow-hidden">
      <div className="flex items-start gap-5">
        <div className="w-9 h-9 rounded-[var(--radius-md)] bg-[var(--color-warm-100)] animate-pulse shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="h-3 w-32 bg-[var(--color-warm-100)] rounded animate-pulse mb-2" />
          <div className="h-9 w-24 bg-[var(--color-warm-100)] rounded animate-pulse mb-1" />
          <div className="h-3 w-20 bg-[var(--color-warm-100)] rounded animate-pulse" />
        </div>
      </div>
      <div className="mt-5 space-y-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i}>
            <div className="flex justify-between mb-1.5">
              <div className="h-3 w-28 bg-[var(--color-warm-100)] rounded animate-pulse" />
              <div className="h-3 w-16 bg-[var(--color-warm-100)] rounded animate-pulse" />
            </div>
            <div className="h-2 w-full bg-[var(--color-warm-100)] rounded-full animate-pulse" />
          </div>
        ))}
      </div>
    </Card>
  );
}

// ─── Main ────────────────────────────────────────────────────────────────

export default function HealthScoreCard({ closeResult, matches, loading = false }) {
  if (loading || !closeResult) {
    return <Skeleton />;
  }

  const subs = computeSubMetrics(closeResult, matches);
  const score = computeScore(subs);
  const band = scoreBand(score);
  const style = BAND_STYLES[band] || BAND_STYLES.neutral;
  const opportunity = topOpportunity(subs);

  // Has data check — when P is 0 we can't meaningfully compute sub-scores.
  const hasData = (subs?.p_cents || 0) > 0;

  // Sub-metric ratios for bar fill (0..1 against target).
  const t1Ratio = Math.min((subs.tier1Rate || 0) / TARGETS.tier1Rate, 1.0);
  const memoRatio = Math.min(
    (subs.memoDiscipline || 0) / TARGETS.memoDiscipline,
    1.0
  );
  // Credit pending: bar = "headroom remaining" (1 at 0%, 0 at threshold).
  const cpRatio = Math.max(
    0,
    1 - (subs.creditPendingPctOfP || 0) / TARGETS.creditPendingPctOfP
  );
  // Aging: bar = "freshness remaining" (1 at 0 days, 0 at threshold).
  const agingRatio =
    subs.agingDays <= 0
      ? 1.0
      : subs.agingDays >= TARGETS.agingDays
      ? 0.0
      : 1 - subs.agingDays / TARGETS.agingDays;

  return (
    <Card padded className={`overflow-hidden border-2 ${style.ringClass}`}>
      {/* Headline row: big score + label + summary chip */}
      <div className={`-m-5 mb-5 p-5 ${style.bgClass}`}>
        <div className="flex items-start gap-5">
          <div
            className={`w-11 h-11 rounded-[var(--radius-md)] flex items-center justify-center shrink-0 ${style.iconWrapClass} [&_svg]:w-[22px] [&_svg]:h-[22px]`}
          >
            <Activity />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-[0.8px] text-[var(--color-warm-500)] mb-1">
              Close Health Score
            </div>
            <div className="flex items-baseline gap-3">
              <span
                className={`text-[36px] font-extrabold tracking-tight tabular-nums leading-none ${style.textClass}`}
              >
                {hasData && score !== null ? score : '—'}
              </span>
              <span className="text-[14px] font-medium text-[var(--color-warm-500)]">
                / 100
              </span>
              {hasData && (
                <Badge tone={style.chipTone} size="sm">
                  {style.summary}
                </Badge>
              )}
            </div>
            {!hasData && (
              <div className="text-[12px] text-[var(--color-warm-500)] mt-1">
                No QBO purchases in this period — score unavailable.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sub-metric bars */}
      <div className="space-y-3.5">
        <SubMetricBar
          label="Tier 1 match rate"
          currentText={hasData ? fmtPct(subs.tier1Rate) : '—'}
          targetText={fmtPct(TARGETS.tier1Rate)}
          ratio={t1Ratio}
          loading={false}
        />
        <SubMetricBar
          label="Memo discipline"
          currentText={hasData ? fmtPct(subs.memoDiscipline) : '—'}
          targetText={fmtPct(TARGETS.memoDiscipline)}
          ratio={memoRatio}
          loading={false}
        />
        <SubMetricBar
          label="Credit Pending"
          currentText={
            hasData
              ? `${fmtMoney(subs.creditPendingCents)} (${fmtPct(subs.creditPendingPctOfP)})`
              : '—'
          }
          targetText={`<= ${fmtPct(TARGETS.creditPendingPctOfP)} of P`}
          ratio={cpRatio}
          loading={false}
        />
        <SubMetricBar
          label="Aging (oldest credit pending)"
          currentText={
            subs.agingDays > 0 ? `${subs.agingDays}d` : 'No pending'
          }
          targetText={`<= ${TARGETS.agingDays}d`}
          ratio={agingRatio}
          loading={false}
        />
      </div>

      {/* Top opportunity / auto-flip footnote */}
      {hasData && (
        <div className="mt-5 pt-4 border-t border-[var(--color-warm-100)]">
          {opportunity ? (
            <div className="flex items-start gap-2.5">
              <div className="w-7 h-7 shrink-0 rounded-[var(--radius-md)] bg-[var(--color-action-100)] text-[var(--color-action-700)] flex items-center justify-center [&_svg]:w-[14px] [&_svg]:h-[14px]">
                <Lightbulb />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-bold uppercase tracking-[0.8px] text-[var(--color-warm-500)] mb-0.5">
                  Top opportunity
                </div>
                <div className="text-[13px] font-semibold text-[var(--color-warm-900)] leading-snug">
                  {opportunity.label}
                </div>
                <div className="text-[12px] text-[var(--color-warm-600)] leading-snug mt-0.5">
                  {opportunity.hint}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-[12px] text-[var(--color-success-700)]">
              <TrendingUp className="w-4 h-4" />
              <span className="font-semibold">All sub-metrics on target.</span>
            </div>
          )}
          <div className="mt-3 text-[11px] text-[var(--color-warm-400)] leading-snug">
            Auto-flip confidence: {fmtPct(subs.autoFlipConfidence)} (Tier 2
            heuristic acceptance rate; 5% of overall score).
          </div>
        </div>
      )}
    </Card>
  );
}
